import {
  BookOpen,
  Bug,
  Candy,
  Coffee,
  CookingPot,
  Cylinder,
  Flame,
  Guitar,
  Lamp,
  Lightbulb,
  Logs,
  NotebookPen,
  Package,
  TentTree,
} from "lucide-react";

import { cn } from "@/engine/support/classNames";

import { type PlaceItemId } from "./campData";

export interface PlaceBounds {
  /** Left edge of the figure body, in percent of the figure box. */
  x0: number;
  /** Right edge of the figure body, in percent of the figure box. */
  x1: number;
  /** Ground line, in percent from the top of the figure box. */
  ground: number;
}

export interface PlacePropsProps {
  slots: Partial<Record<"near" | "side" | "air", PlaceItemId>>;
  bounds?: PlaceBounds | undefined;
  className?: string | undefined;
}

const DEFAULT_BOUNDS: PlaceBounds = { x0: 16, x1: 84, ground: 96 };

const icons = {
  mug: Coffee,
  lantern: Lamp,
  stick: TentTree,
  guitar: Guitar,
  marsh: Candy,
  book: BookOpen,
  notes: NotebookPen,
  pack: Package,
  logs: Logs,
  kettle: CookingPot,
  stump: Cylinder,
  lamp: Lightbulb,
  sparks: Flame,
  moths: Bug,
} as const;

function PropIcon({
  item,
  className,
}: {
  item: PlaceItemId;
  className?: string;
}) {
  const Icon = icons[item];
  return <Icon aria-hidden className={cn("size-auto", className)} />;
}

/** Three ground-aware decoration slots around an animal figure. */
export function PlaceProps({
  slots,
  bounds = DEFAULT_BOUNDS,
  className,
}: PlacePropsProps) {
  return (
    <div
      aria-hidden
      className={cn("pointer-events-none absolute inset-0 z-20", className)}
    >
      {slots.side ? (
        <span
          className="absolute -translate-x-full -translate-y-full text-primary [&>svg]:h-[18%] [&>svg]:w-auto"
          style={{ left: `${bounds.x0}%`, top: `${bounds.ground}%` }}
        >
          <PropIcon item={slots.side} />
        </span>
      ) : null}
      {slots.near ? (
        <span
          className="absolute -translate-y-full text-primary [&>svg]:h-[18%] [&>svg]:w-auto"
          style={{ left: `${bounds.x1}%`, top: `${bounds.ground}%` }}
        >
          <PropIcon item={slots.near} />
        </span>
      ) : null}
      {slots.air ? (
        <span className="absolute inset-x-0 top-0 flex items-start justify-between text-attention [&>svg]:h-[13%] [&>svg]:w-auto">
          <PropIcon item={slots.air} />
          <PropIcon item={slots.air} />
        </span>
      ) : null}
    </div>
  );
}
